
import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { BOOT_LOCALE, LL } from 'i18n/i18n.service';
import { AuthService } from 'services/auth.service';
import { getSupportEmail } from 'environments/environment-helpers';
import { getReadableAppVersion } from 'environments/version-getters';
import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import { DeferredPromise } from '@cf-shared/shared-util';
import { CsPage } from 'providers/nav-controller';
import { getMyApp } from 'app/app-interface';
import { isAppStandalone, isPlatformIOS, isCapacitorNative } from 'environments/environment-helpers';
import { getSharedUrlOrigin } from 'util/backend-origin';
import { TelemetryService } from 'services/telemetry.service';
import { CloudFunctionsService } from 'services/cloud-functions.service';
import { DateTime } from 'luxon';
import { LoadingController } from 'providers/loading-controller';
import { MenuController } from 'providers/menu-controller';
import { ExternalFlowService, SignInTokenError } from 'services/external-flow.service';
import { UrlQueryService } from 'services/url-query-service';
import { startCase } from 'lodash-es';
import { LoginPageParams } from './login-interface';
import { TranslatedString, untranslatedString } from 'models/translated-string';
import { CoachClaimService } from 'services/coach-claim.service';
import { CoachInvitationWording, coachInvitationName, decideCoachInvitationWording } from 'services/coach-claim-helpers';
import { CoachTokenCheckOutcome } from 'services/coach-claim-failure-policy';
import { CoachBrandingThemeService } from 'services/coach-branding-theme.service';
import { AccountHintService } from 'services/account-hint.service';
import { LoggingService } from 'services/logging.service';
import { openExternalLink, APPLE_EULA_URL } from 'util/open-external-link';
import { describeErrorForLog } from '@cf-shared/error-describe';
import { gateSignInTapAsync, SignInTapGate } from 'services/popup-resolver-warmup';
import {
  ExistingAccountLookupOutcome,
  classifyExistingAccountLookup,
  isSameAccountAsSignedIn,
  shouldOfferExistingAccountRecovery,
} from 'services/new-social-account-guard';
import {
  EmailSignInOutcome,
  SignInMethodsLookupFailure,
  answerExistingAccountAsync,
  isSignInLinkAddressRefused,
  lookUpSignInMethodsAsync,
  signInWithEmailSequenceAsync,
} from './email-sign-in-sequence';

enum LoginPhase {
  signIn = 'signIn',
  newRegistration = 'newRegistration',
  useEmailLink = 'useEmailLink',
  emailLinkSent = 'emailLinkSent',
  subscriptionSuccess = 'subscriptionSuccess',
  landing = 'landing',
  coachClaim = 'coachClaim',
}

/**
 * The social sign-in providers the login page offers.
 *
 * The string values are not free choices. They are what the login telemetry
 * event already records as its `method` property, and what `oauthErrorAsync`
 * already reported, so changing one would change telemetry that is being read.
 *
 * Do not write the telemetry call's own name in this comment. The lexicon gate
 * (`scripts/ci/check-telemetry-lexicon.mjs`) scans the raw source text and does
 * not strip comments, so a call spelled out here is read as a real call site
 * whose properties it cannot resolve, and the push is refused.
 */
enum SocialSignInProvider {
  Google = 'google',
  Apple = 'apple',
  Facebook = 'facebook',
}

/** For a sign-in path with no #4936 question to offer: answers "nothing was asked". */
const NO_SIGN_IN_OFFER = async (): Promise<boolean> => false;

/**
 * Which of the coach-invitation screens is showing, inside LoginPhase.coachClaim.
 *
 * One value rather than a pair of booleans because the states are mutually exclusive, and two
 * booleans can express combinations that mean nothing ("checking" and "rejected" at once).
 *
 * `CouldNotCheck` is the one worth explaining. It is NOT a milder `LinkRejected`: it means we
 * never found out, so it must not call the link bad and it must offer a retry. Before it existed,
 * a dropped connection landed on `LinkRejected` and told a brand-new client that a perfectly good
 * invitation was dead, with only a Cancel button to press.
 */
enum CoachClaimScreen {
  /** Asking the server about the link. */
  Checking = 'checking',
  /** The link is good: show the coach's name and the Sign In / Create Account choice. */
  Offer = 'offer',
  /** The server looked at the link and said no. Only this state may say the link is bad. */
  LinkRejected = 'link-rejected',
  /** Nobody reached a verdict. Say so, and offer Try Again. */
  CouldNotCheck = 'could-not-check',
}

@Component({
    selector: 'page-login',
    templateUrl: 'login.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginPage extends CsPage<LoginPageParams> {
  protected LL = LL;
  email: string = '';
  realName: string = '';
  password: string = '';
  useOTL: boolean; // using one-time-link for login
  loginError: TranslatedString = untranslatedString('');
  // whether loginError holds the #6743 "sign-in is ready, tap again" prompt, which the next tap clears
  private showingTapAgainPrompt = false;
  phase = LoginPhase.newRegistration;
  LoginPhase = LoginPhase; // needed for the template access
  waiting = false; // used to show spinner

  version: string = getReadableAppVersion();
  supportEmail = getSupportEmail();
  // on Capacitor native, bare paths resolve to localhost — use server origin
  tosUrl = getSharedUrlOrigin() + '/tos.html';
  privacyUrl = getSharedUrlOrigin() + '/privacy.html';
  refundUrl = getSharedUrlOrigin() + '/refund.html';
  eulaUrl = APPLE_EULA_URL;
  supportMailUrl = "mailto:" + getSupportEmail();

  // on capacitor native, intercept legal-link taps and route through
  // browser.open (in-app safari overlay) — see open-external-link.ts
  // for why. on web, do nothing: the native <a target="_blank">
  // handles middle-click / right-click "open in new tab" affordances.
  handleLegalLink(ev: MouseEvent, url: string) {
    if (!isCapacitorNative()) return;
    ev.preventDefault();
    openExternalLink(url).catch(err => this.logging.trackAbnormalException(err));
  }


  isWebView = false;
  isLoggedIn = false; // login page is shown even when logged in - when in webview
  allowLinkSignIn = false;
  // Sign-in-with-Apple has two button paths in the template.
  //
  // `isIOSNative` gates the native iOS button (the @if (isIOSNative)
  // block in login.html), which goes through the @capgo/capacitor-social-login
  // plugin's native SiwA flow. Plain isPlatformIOS() also matches iPhone
  // Safari, so we AND with isCapacitorNative() to keep the native-only
  // button off the web.
  //
  // `isWeb` gates the web popup button (the @if (showAppleLoginOnWeb
  // && isWeb) block), which goes through Firebase's web OAuth popup
  // (signInWithPopup). "Web" here means "not running inside Capacitor"
  // - true for iPhone Safari, Android Chrome, and desktop PWA alike.
  // Hiding the web button on every Capacitor-native shell mirrors the
  // init-side iOS-positive gate in app.component.ts, which only
  // attaches the apple block to SocialLogin.initialize when
  // isPlatformIOS() is true. On any other Capacitor-native shell
  // (Android today, hypothetical future platforms) the apple provider
  // is therefore never registered, and SocialLogin.login({ provider:
  // 'apple' }) would throw "Cannot find provider 'apple'. Provider was
  // not initialized." (see #3678 for the same failure family with
  // Google, when the apple block was briefly added unconditionally and
  // bricked Android Google sign-in).
  isIOSNative = isCapacitorNative() && isPlatformIOS();
  isWeb = !isCapacitorNative();
  // Web Sign-in-with-Apple is enabled in every environment. The Firebase
  // Apple-provider OAuth code-flow config (Services IDs
  // app.mhphysique.fitness(.dev).siwa-relay, Team ZX4R6CC5U9, SiwA Auth
  // Keys) is populated for BOTH the dev and prod Firebase projects —
  // wired 2026-05-28 via PRD #3532 (siwa-auth-key-phase1-portal-setup).
  // See design-docs/mobile-app-accounts.md for the wiring tables.
  showAppleLoginOnWeb = true;

  // coach claim state
  claimCoachName = '';
  claimWording = CoachInvitationWording.ProgramPrepared;
  claimScreen = CoachClaimScreen.Checking;
  CoachClaimScreen = CoachClaimScreen; // needed for the template access
  // Tier 1 white-labeling (#3707): coach branding shown on the pre-claim
  // screen, sourced from coachValidateClaimToken (no client DB exists yet).
  claimBrandLogoUrl = '';
  claimBrandName = '';

  // populated when mode === 'subscription-success'; cs-store-badges renders each badge
  // only when its corresponding URL is present
  androidInstallUrl?: string;
  iosInstallUrl?: string;

  constructor(
    private alertCtrl: AlertController,
    private menuCtrl: MenuController,
    private loadingCtrl: LoadingController,
    private auth: AuthService,
    private telemetry: TelemetryService,
    private cloudFunctions: CloudFunctionsService,
    private externalFlow: ExternalFlowService,
    private cdr: ChangeDetectorRef,
    private urlQuery: UrlQueryService,
    private coachClaim: CoachClaimService,
    private logging: LoggingService,
    private coachBrandingTheme: CoachBrandingThemeService,
    private accountHint: AccountHintService,
  ) {
    super();
  }

  isParamsValid(params: LoginPageParams): boolean {
    if (!('mode' in params)) return true;
    return params.mode == 'login' || params.mode == 'register' || params.mode == 'otl-failed' || params.mode == 'otl-sent' || params.mode == 'landing' || params.mode == 'subscription-success';
  }

  async ionViewCanEnter(): Promise<any> {
    this.isWebView = this.auth.isWebView();

    /*
    The best way to be sure that no data is leaked between users is to reload the app after logging out.
    But we cannot just reload anywhere because it would either cause a flicker - or if we try to show a progress
    while reloading, it will internally throw "invalid views to insert".

    So, logout process will internally show the booting page and then redirect back to the login page,
    but if it was already used once in this app instance, the below will force it to reload before the login page is
    displayed, and the boot process will then properly land it back to this same location with no flickering,
    just the data will be cleared (which we are sure based on the fact that this static member which is
    never set back to false - will be false).
    */
    if (this.auth.getWasEverLoggedIn() && !this.isWebView) { // webview can't logout, and forces the login page even when logged in
      console.log('LoginPage: Forcing refresh...');
      await getMyApp().reloadAppAsync({ resetToRoot: false, clearLocalData: true }); // this never returns anyway (so perhaps the below DeferredPromise is not needed?)
      // this will make ionic wait forever, i.e - until the app is reloaded
      return DeferredPromise.defer<void>();
    } else {
      console.log('LoginPage: Showing login...');
      // load Google's sign-in frame now, so a tap on a social button opens its popup straight
      // away; start-up no longer loads it (#6743). Does nothing on native, never throws.
      void this.auth.warmSignInPopupAsync();
    }

    let otlError = () => {
      if (this.viewDestroyed) return;
      this.loginError = LL.auth.errors.otlExpired();
      this.phase = LoginPhase.useEmailLink;
      this.cdr.markForCheck();
    }
    let otlRecent = () => {
      if (this.viewDestroyed) return;
      this.loginError = LL.auth.errors.otlRecent();
      this.phase = LoginPhase.useEmailLink;
      this.cdr.markForCheck();
    }

    // Sign-in token from magic link emails is processed during boot (appBootSequenceAsync)
    // before the login page is ever shown. If it failed, signInTokenFailed flag is set.

    let mode = this.navParams.data.mode || 'login';

    this.isLoggedIn = !!this.auth.user;

    // if a coach invite token is present in URL query, enter the claim flow
    if (this.coachClaim.hasPendingClaimToken()) {
      console.log(`[CLAIM] login page: detected pending claim token`);
      this.phase = LoginPhase.coachClaim;
      this.claimScreen = CoachClaimScreen.Checking;
      this.safeMarkForCheck(this.cdr);
      void this.validateClaimTokenAsync();
      return true;
    }

    switch (mode) {
      case 'landing':
        this.phase = LoginPhase.landing;
        break;
      case 'login':
        this.phase = LoginPhase.signIn;
        break;
      case 'register':
        this.phase = LoginPhase.newRegistration;
        break;
      case 'otl-failed':
        otlError();
        break;
      case 'otl-sent':
        this.phase = LoginPhase.emailLinkSent
        break;
      case 'subscription-success':
        this.phase = LoginPhase.subscriptionSuccess;
        this.androidInstallUrl = this.navParams.data.androidInstallUrl;
        this.iosInstallUrl = this.navParams.data.iosInstallUrl;
        break;
      default: {
        let tokenError = this.externalFlow.consumeSignInTokenError();
        if (tokenError) {
          // show an appropriate alert based on the error type
          if (tokenError === SignInTokenError.Network) {
            await this.alertCtrl.showAlertAsync({
              message: LL.auth.errors.firebase.networkRequestFailed(),
              buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary }]
            });
          } else if (tokenError === SignInTokenError.Other) {
            await this.alertCtrl.showAlertAsync({
              message: LL.auth.errors.signInTokenCheck(),
              buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary }]
            });
          }

          // on iOS standalone, can't use email links — fall back to password login
          if (isPlatformIOS() && isAppStandalone()) {
            this.phase = LoginPhase.signIn;
          } else {
            otlError();
          }
        } else {
          this.phase = LoginPhase.signIn;
        }
        break;
      }
    }

    // webview is limited to only particular phases
    if (this.isWebView) {
      const phaseRemapTable: { [key: string]: LoginPhase } = {
        [LoginPhase.signIn]: LoginPhase.useEmailLink,
        [LoginPhase.newRegistration]: LoginPhase.useEmailLink,
        [LoginPhase.emailLinkSent]: LoginPhase.emailLinkSent,
        [LoginPhase.subscriptionSuccess]: LoginPhase.subscriptionSuccess,
        [LoginPhase.useEmailLink]: LoginPhase.useEmailLink,
      };
      this.phase = phaseRemapTable[this.phase] || LoginPhase.useEmailLink;
      if (this.auth.user) {
        this.email = this.auth.user.email;
      }
    }

    // check for recent email links
    if (this.auth.couch && this.auth.couch.magicLinkLastSent_utc) {
      let diff = DateTime.utc().diff(DateTime.fromISO(this.auth.couch.magicLinkLastSent_utc));
      if (diff.as('minutes') < 5) {
        otlRecent();
      }
    }

    // Handle auto-send link scenario from subscribe page
    if (this.navParams.data.autoSendLink && this.navParams.data.email) {
      this.email = this.navParams.data.email;
      // Set phase to show email link UI
      this.phase = LoginPhase.useEmailLink;
      // Automatically send the magic link
      this.safeTimeout(() => {
        void this.loginViaOTLAsync();
      }, 500); // Small delay to ensure UI is ready
    }

    // #5895: an onboarding link names the account it was sent to. Offering that address in
    // the email field heads off a password sign-in into a different account before it starts.
    const hintedEmail = this.accountHint.peek();
    if (hintedEmail && this.phase === LoginPhase.signIn && !this.email) {
      this.email = hintedEmail;
    }

    this.safeMarkForCheck(this.cdr);

    return true;
  }

  async ionViewWillEnter() {
    this.menuCtrl.enable(false);
    // don't allow sign in with link from iOS installed app
    this.allowLinkSignIn = !(isPlatformIOS() && isAppStandalone());
  }

  async ionViewWillLeave() {
    this.menuCtrl.enable(true);
  }

  handleResetForm() {
    this.phase = LoginPhase.signIn;
    this.waiting = false;
    this.email = '';
    this.realName = '';
    this.password = '';
    this.loginError = untranslatedString('');

    this.cdr.markForCheck();
  }

  async offerAlternativeSignInAsync(methods: string[]): Promise<boolean> {
    // check if user registered for google or facebook
    let alternativeProvider: string;
    if (methods.some(m => m == 'google.com')) {
      alternativeProvider = 'Google';
    } else if (methods.some(m => m == 'facebook.com')) {
      alternativeProvider = 'Facebook';
    }

    if (alternativeProvider) {
      let result = await this.alertCtrl.showAlertAsync({
        title: LL.auth.errors.useAlternativeTitle({ provider: alternativeProvider }),
        message: LL.auth.errors.useAlternativeMessage({ provider: alternativeProvider }),
        enableBackdropDismiss: true,
        buttons: [
          {
            text: LL.common.buttons.yes(),
            role: AlertButtonRole.Primary,
            result: () => {
              if (alternativeProvider == 'Google') {
                void this.loginWithGoogle();
              } else if (alternativeProvider == 'Facebook') {
                void this.loginWithFacebook();
              }
              return true;
            },
          },
          {
            text: LL.common.buttons.no(),
            role: AlertButtonRole.Cancel,
            result: false,
          },
        ],
      });

      return result;
    }

    return false;
  }

  handleLoginViaEmail() {
    void this.loginViaEmailAsync();
    return false;
  }
  async loginViaEmailAsync() {
    if (!this.email) {
      return;
    }

    let credentials = {
      email: this.email,
      password: this.password || '',
    };

    this.waiting = true;
    try {
      const outcome = await signInWithEmailSequenceAsync({
        lookUpMethods: () => this.auth.getMethodsForEmailAsync(credentials.email),
        reportLookupFailure: failure => this.reportSignInMethodsLookupFailure(failure),
        offerAlternativeSignIn: methods => this.offerAlternativeSignInAsync(methods),
        signIn: () => this.auth.signInWithEmailAsync(credentials),
      });
      if (outcome === EmailSignInOutcome.AlternativeChosen) {
        return;
      }
      // recorded before the finish step, which can wait on a dialog (#5895)
      this.telemetry.trackEvent('login', { method: 'email' });
      await this.finishSignInAsync(() => this.offerAfterPasswordSignInAsync());
    } catch (error) {
      this.waiting = false;
      // refresh now so the button stops reading "Processing" while the error alert is open
      this.safeMarkForCheck(this.cdr);
      await this.handleAuthErrorAsync(error);
    }

    this.safeMarkForCheck(this.cdr);
  }

  handleLoginViaOTL() {
    void this.loginViaOTLAsync();
    return false;
  }
  async loginViaOTLAsync() {
    if (!this.email) {
      return;
    }

    this.waiting = true;

    try {
      // on Capacitor, location.origin is https://localhost — use the real app domain
      const baseUrl = getSharedUrlOrigin();
      const result = await this.cloudFunctions.signInByLink({
        email: this.email,
        newUser: false,
        baseUrl,
        trackingParams: this.urlQuery.getTrackingDataAsUrlParams(),
        // tell the server which language to write the email in. BOOT_LOCALE is
        // the right source because it is resolved before sign-in and survives
        // sign-out, so it is available here on the logged-out login page (#4244).
        uiLanguage: BOOT_LOCALE,
      });
      // if this address has SEVERAL accounts on it, the server deliberately sent
      // NO link, because a link can only sign them into one account and it cannot
      // tell which one is theirs (#4505). Say so instead of showing the
      // "check your email" screen for an email that was never sent.
      if (result?.accountAmbiguous) {
        this.waiting = false;
        this.loginError = LL.auth.errors.otlAmbiguousAccount({ email: getSupportEmail() });
        this.safeMarkForCheck(this.cdr);
        return;
      }
      this.telemetry.trackEvent('login_attempt', { method: 'otl' });
      this.waiting = false;
      this.phase = LoginPhase.emailLinkSent;
    } catch (error) {
      this.waiting = false;
      // if the server refused the address as malformed (fitness-app#1727), say so under
      // the field, the same way a Firebase `auth/invalid-email` is shown
      if (isSignInLinkAddressRefused(error)) {
        this.loginError = LL.auth.errors.firebase.invalidEmail();
      } else {
        await this.handleAuthErrorAsync(error);
      }
    }

    this.safeMarkForCheck(this.cdr);
  }

  /**
   * The last step of every successful sign-in on this page: run whatever dialog
   * applies, and finish the login only when no dialog was shown.
   *
   * `offerAsync` answers whether it put a dialog on screen. The dialog and the
   * boot splash cannot both be on screen: <popover-host> lives INSIDE app.html's
   * `@if (!appLoading)` block, and authorizationCheckAsync ends in
   * loginCompleteAsync, whose only job is to raise that splash. So it runs only
   * when no dialog was shown. Nothing is lost when it is skipped — the splash is
   * a placeholder, and the app component navigates on the auth-state transition
   * either way. Calling it AFTER a dialog would be worse than skipping: by then
   * the boot may have finished and lowered the splash, and raising it again would
   * leave nothing to take it down (#4936).
   *
   * Every sign-in path goes through here, so a dialog added to any of them
   * cannot be destroyed by the splash just because one path forgot the rule.
   */
  private async finishSignInAsync(offerAsync: () => Promise<boolean>): Promise<void> {
    const askedSomething = await offerAsync();
    if (!askedSomething) await this.authorizationCheckAsync();
  }

  /**
   * The question that can follow a password sign-in or registration: only the account
   * hint's (#5895). The #4936 question is for social sign-ins, where Firebase can make an
   * account the person did not ask for; a password account is only ever made on purpose.
   */
  private offerAfterPasswordSignInAsync(): Promise<boolean> {
    return this.accountHint.offerAfterSignInAsync({
      signedInEmail: this.auth.getSignedInFirebaseEmail(),
      userPressedSignIn: this.phase === LoginPhase.signIn,
      accountWasJustCreated: false,
    }, NO_SIGN_IN_OFFER);
  }

  /** After authenticating with Firebase, perform internal checks if needed. */
  private async authorizationCheckAsync() {
    try {
      // No authorization checks at the moment
      // we used to use this to check if the user has invite
    } catch (error) {
      console.error('Error while checking autorization: ', describeErrorForLog(error));
      await this.handleAuthErrorAsync(error);
      return;
    }
    await getMyApp().loginCompleteAsync();
  }

  handleSignUp() {
    void this.signUpAsync();
    return false;
  }
  async signUpAsync() {
    this.loginError = untranslatedString('');
    this.waiting = true;
    if (!this.email || !this.email.match(/^.+\@.*[^.].*\.[^.]{2,}/)) {
      this.loginError = LL.auth.errors.badEmailFormat();
      this.waiting = false;
      this.cdr.markForCheck();
      return;
    }
    try {
      // a failed lookup answers no methods, so account creation still goes ahead (#5472);
      // an existing account then lands in the auth/email-already-in-use fallback below
      let methods = await lookUpSignInMethodsAsync(
        () => this.auth.getMethodsForEmailAsync(this.email),
        failure => this.reportSignInMethodsLookupFailure(failure),
      );
      console.log('Login methods available: ', JSON.stringify(methods));
      // no methods means the user is not created yet
      if (methods.length <= 0) {
        // nothing here for now
      }
      if (methods.some(m => m != 'password')) {
        if (await this.offerAlternativeSignInAsync(methods)) {
          this.safeMarkForCheck(this.cdr);
          return;
        }
      }
    } catch (error) {
      this.waiting = false;
      await this.handleAuthErrorAsync(error);
      return;
    }

    // if user didn't supply real name, provide a default one based on the email
    if (!this.realName) {
      this.realName = startCase((this.email.match(/[^@]+/) || ['unknown'])[0].replace(/[^\w\d]/g, ' '));
    }
    let credentials = {
      email: this.email,
      displayName: this.realName,
      password: this.password || '',
    };
    try {
      let user = await this.auth.signUpAsync(credentials);
      console.log('User created: ', JSON.stringify(user));
      // recorded before the finish step, which can wait on a dialog (#5895)
      this.telemetry.trackEvent('login', { method: 'email' });
      await this.finishSignInAsync(() => this.offerAfterPasswordSignInAsync());
    } catch (error) {
      if (error.code == 'auth/email-already-in-use') {
        try {
          await this.auth.signInWithEmailAsync(credentials);
          // recorded before the finish step, which can wait on a dialog (#5895)
          this.telemetry.trackEvent('login', { method: 'email' });
          await this.finishSignInAsync(() => this.offerAfterPasswordSignInAsync());
          this.safeMarkForCheck(this.cdr);
          return;
        } catch (error2) {
          // silent retry failed (different password / OAuth-only account); user still gets the friendly emailAlreadyInUse message via handleAuthErrorAsync below
          this.logging.trackSilentProblem('LoginPage.signupAutoSignInFallbackFailed', {
            outerCode: error.code,
            innerCode: error2?.code,
            innerMessage: error2?.message,
          });
        }
      }

      this.waiting = false;
      await this.handleAuthErrorAsync(error);
    }

    this.safeMarkForCheck(this.cdr);
  }

  switchToPhase(phase: LoginPhase) {
    this.email = '';
    this.realName = '';
    this.password = '';
    this.loginError = untranslatedString('');
    this.phase = phase;
    this.cdr.markForCheck();
  }

  handleForgotPassword() {
    void this.forgotPasswordAsync();
  }
  async forgotPasswordAsync() {
    console.log(`Sending password reset email to ${this.email}`);
    try {
      this.waiting = true;
      await this.auth.resetPasswordAsync(this.email);
      this.waiting = false;
      this.password = '';
      this.loginError = LL.auth.errors.passwordResetSent();
    } catch (error) {
      this.waiting = false;
      await this.handleAuthErrorAsync(error);
    }

    this.safeMarkForCheck(this.cdr);
  }

  handleOneTimeLink() {
    this.useOTL = !this.useOTL;
    this.cdr.markForCheck();
  }

  // The three social buttons are three thin callers of one shared body. Keeping
  // the names means login.html and offerAlternativeSignInAsync call what they
  // always called.
  async loginWithGoogle() {
    await this.signInWithProviderAsync(SocialSignInProvider.Google);
  }

  async loginWithApple() {
    await this.signInWithProviderAsync(SocialSignInProvider.Apple);
  }

  async loginWithFacebook() {
    await this.signInWithProviderAsync(SocialSignInProvider.Facebook);
  }

  /**
   * The only two things a social sign-in does differently per provider: which
   * spinner text to show, and which AuthService method to call.
   *
   * Looked up explicitly rather than built from the provider name by string
   * concatenation, so both stay statically checked — a typesafe-i18n accessor
   * assembled at runtime is not checked at all, and neither is a method picked
   * off `this.auth` by a computed key.
   */
  private socialSignInSteps(provider: SocialSignInProvider): { loadingText: TranslatedString, signInAsync: () => Promise<any> } {
    switch (provider) {
      case SocialSignInProvider.Google:
        // the hinted account, if a link named one, is preselected in Google's account chooser (#5895)
        return { loadingText: LL.common.loading.loggingInWithGoogle(), signInAsync: () => this.auth.signInWithGoogleAsync({ loginHint: this.accountHint.peek() }) };
      case SocialSignInProvider.Apple:
        return { loadingText: LL.common.loading.loggingInWithApple(), signInAsync: () => this.auth.signInWithAppleAsync() };
      case SocialSignInProvider.Facebook:
        return { loadingText: LL.common.loading.loggingInWithFacebook(), signInAsync: () => this.auth.signInWithFacebookAsync() };
    }
  }

  /**
   * Signs in with one of the social providers. One body for all three, so a
   * change to how social sign-in behaves is one edit rather than three that have
   * to stay in step, with nothing checking that they did.
   */
  private async signInWithProviderAsync(provider: SocialSignInProvider) {
    const { loadingText, signInAsync } = this.socialSignInSteps(provider);
    // read which screen they are on BEFORE signing in, because the guard below
    // asks what the user set out to do and the phase can move once they are in
    const userPressedSignIn = this.phase == LoginPhase.signIn;
    // the "tap again" prompt from an earlier tap has done its job once the user taps again
    if (this.showingTapAgainPrompt) {
      this.showingTapAgainPrompt = false;
      this.loginError = untranslatedString('');
    }
    let loading = this.loadingCtrl.create({ content: loadingText });
    void loading.present();
    try {
      // web: if Google's sign-in frame is still loading, a popup opened after that wait can be
      // blocked by Safari (#6743). Wait under the spinner, then ask for a second tap, which opens
      // the popup straight away. If the frame did not load, carry on to the popup as before.
      if (await gateSignInTapAsync(this.auth) == SignInTapGate.AskToTapAgain) {
        void loading.dismiss();
        if (this.viewDestroyed) return;
        this.loginError = LL.auth.signInReadyTapAgain();
        this.showingTapAgainPrompt = true;
        this.safeMarkForCheck(this.cdr);
        return;
      }
      const credential = await signInAsync();
      // take the spinner down before the dialog so the two overlays cannot stack
      await loading.dismiss();
      // Inside the try, because this event means the sign-in SUCCEEDED — a
      // sign-in that threw must not reach it.
      this.telemetry.trackEvent('login', { method: provider });
      // offered without waiting for it, so nothing else about signing in waits
      // on the user getting round to answering the dialog
      const signIn = this.auth.describeSocialSignIn(credential);
      void this.finishSignInAsync(() => this.accountHint.offerAfterSignInAsync(
        { signedInEmail: signIn.signedInEmail, userPressedSignIn, accountWasJustCreated: signIn.accountWasJustCreated },
        () => this.offerExistingAccountRecoveryAsync(credential, userPressedSignIn),
      ));
    } catch (error) {
      void loading.dismiss();
      void this.oauthErrorAsync(error, provider);
    }
  }

  /**
   * Offers a way back to an account the user already owns, when a social
   * sign-in has just made them a brand-new one instead (#4936).
   *
   * The case this exists for: somebody buys a subscription on the web under
   * their own address, then opens the app, presses "Sign in", and uses Sign in
   * with Apple with Apple's "Hide My Email". Apple hands us a relay address
   * rather than theirs, so Firebase makes a SECOND account that holds no
   * subscription, and the app shows them the paywall they have already paid
   * past. It reads to them as the app refusing to log them in.
   *
   * The app CANNOT work out that the other account exists — a relay address has
   * no recoverable link to the real one. So it asks, and only the address the
   * person gives us can be checked. `shouldOfferExistingAccountRecovery` is
   * what keeps this from becoming noise: it fires only when signing in produced
   * a new account, never when registering did.
   *
   * Nothing is deleted and nobody is signed out. The user stays on the new
   * account while they fetch the email, and tapping the link runs the
   * account-switch confirmation that already exists in `deep-link-signin.ts`.
   */
  private async offerExistingAccountRecoveryAsync(credential: unknown, userPressedSignIn: boolean): Promise<boolean> {
    try {
      return await this.runExistingAccountRecoveryAsync(credential, userPressedSignIn);
    } catch (error) {
      // this offer is a safety net, never a gate. A failure inside it must not
      // strand a sign-in that actually worked — without this catch the caller's
      // own catch would fire, which both skips the routing that finishes the
      // login AND tells the user their sign-in failed when it did not. It still
      // reaches Sentry, so the failure is not swallowed.
      this.logging.trackSilentProblem('LoginPage.existingAccountRecoveryFailed', {
        code: (error as any)?.code,
        message: (error as any)?.message ?? String(error),
      });
      console.error('Login: the existing-account recovery offer failed:', describeErrorForLog(error));
      // Report that the dialog DID run. Nothing above the dialog can throw —
      // describeSocialSignIn catches its own errors and the decision beside it is
      // pure — so anything reaching here happened once the dialog was already on
      // screen, by which time the boot may have finished and lowered the splash.
      // Raising it then would leave nothing to take it down, and skipping it
      // costs only a placeholder.
      return true;
    }
  }

  /** The body of the recovery offer. See `offerExistingAccountRecoveryAsync` (#4936). */
  private async runExistingAccountRecoveryAsync(credential: unknown, userPressedSignIn: boolean): Promise<boolean> {
    const signIn = this.auth.describeSocialSignIn(credential);
    if (!shouldOfferExistingAccountRecovery({
      userPressedSignIn,
      accountWasJustCreated: signIn.accountWasJustCreated,
    })) return false;

    // Keep asking until the user either gets somewhere or chooses to stay on the
    // new account. This offer is the ONLY one they get — it is armed by the
    // account having just been created, and on any later attempt that account
    // exists, so the dialog never appears again. Without the loop a mistyped
    // address or a dropped connection would spend their one chance, and two of
    // the messages below tell them to try again, which they could not do.
    // It cannot spin: every pass waits on this dialog, and an empty box leaves.
    let prompt = LL.auth.newAccountMade.message();
    for (;;) {
      const typedEmail = await this.askForOtherAccountEmailAsync(prompt);

      // an empty box means they chose to carry on as the new account
      const email = (typedEmail ?? '').trim();
      if (!email) return true;

      // if they typed the address they were just put on, a link would only bring
      // them back to the same empty account
      if (isSameAccountAsSignedIn(email, signIn.signedInEmail)) {
        prompt = LL.auth.newAccountMade.sameAccount();
        continue;
      }

      switch (classifyExistingAccountLookup(await this.lookUpOtherAccountAsync(email))) {
        case 'send-link': {
          const retryPrompt = await this.sendRecoveryLinkAndReportAsync(email);
          if (!retryPrompt) return true;
          prompt = retryPrompt;
          continue;
        }
        case 'no-account':
          prompt = LL.auth.newAccountMade.noAccount();
          continue;
        case 'ambiguous':
          await this.showRecoveryMessageAsync(LL.auth.newAccountMade.severalAccounts());
          return true;
        case 'lookup-failed':
          prompt = LL.auth.newAccountMade.couldNotCheck();
          continue;
      }
    }
  }

  /**
   * Asks for the address the user subscribed under (#4936).
   *
   * `prompt` carries the message: on the first pass it explains what happened,
   * and on a retry it is the reason the previous attempt got nowhere — so the
   * box comes back with the problem stated above it and the address still to
   * type. Returns an empty value when the user chose to stay on the new account.
   */
  private async askForOtherAccountEmailAsync(prompt: TranslatedString): Promise<string> {
    return await this.alertCtrl.showAlertAsync<string>({
      title: LL.auth.newAccountMade.title(),
      message: prompt,
      inputs: [{ name: 'email', type: 'email', placeholder: LL.auth.newAccountMade.placeholder() }],
      buttons: [
        { text: LL.auth.newAccountMade.continueAsNew(), role: AlertButtonRole.Cancel, result: null },
        { text: LL.auth.newAccountMade.findMyAccount(), role: AlertButtonRole.Primary, result: (data: any) => data.email },
      ],
      implicitCancelResult: null,
    });
  }

  /** Asks the server whether any account holds this address (#4936). */
  private async lookUpOtherAccountAsync(email: string): Promise<ExistingAccountLookupOutcome> {
    const loading = this.loadingCtrl.create({ content: LL.auth.newAccountMade.checking() });
    void loading.present();
    try {
      const found = await this.cloudFunctions.checkUserExistsAsync({ email });
      return { kind: 'answered', exists: found.exists, ambiguous: found.ambiguous };
    } catch (error) {
      // the check can fail rather than answer, and "we could not look" must not
      // become "you have no other account" — that would tell a paying customer
      // their account is gone
      this.logging.trackSilentProblem('LoginPage.existingAccountLookupFailed', {
        code: (error as any)?.code,
        message: (error as any)?.message ?? String(error),
      });
      console.error('Login: existing-account lookup failed:', describeErrorForLog(error));
      return { kind: 'failed' };
    } finally {
      await loading.dismiss();
    }
  }

  /**
   * Sends the link and tells the user what happened (#4936).
   *
   * Returns the message to re-ask with when the attempt is worth repeating, and
   * nothing when the flow is over — either because the link went out, or because
   * only a person can help from here.
   */
  private async sendRecoveryLinkAndReportAsync(email: string): Promise<TranslatedString> {
    const outcome = await this.sendRecoveryLinkAsync(email);
    if (outcome === 'sent') {
      await this.showRecoveryMessageAsync(LL.auth.newAccountMade.linkSent({ email }));
      return null;
    }
    // the server refuses to send when several accounts hold the address (#4505),
    // so there is nothing to retry — they need a person
    if (outcome === 'ambiguous') {
      await this.showRecoveryMessageAsync(LL.auth.newAccountMade.severalAccounts());
      return null;
    }
    return LL.auth.newAccountMade.couldNotSend();
  }

  /** Emails a sign-in link to the account the user says is really theirs (#4936). */
  private async sendRecoveryLinkAsync(email: string): Promise<'sent' | 'ambiguous' | 'failed'> {
    try {
      const sent = await this.cloudFunctions.signInByLink({
        email,
        newUser: false,
        baseUrl: getSharedUrlOrigin(),
        uiLanguage: BOOT_LOCALE,
      });
      // the server refuses to send when several accounts hold the address
      // (#4505), so a "check your email" here would promise an email that was
      // never sent
      return sent?.accountAmbiguous ? 'ambiguous' : 'sent';
    } catch (error) {
      this.logging.trackSilentProblem('LoginPage.recoveryLinkSendFailed', {
        code: (error as any)?.code,
        message: (error as any)?.message ?? String(error),
      });
      console.error('Login: could not send the recovery sign-in link:', describeErrorForLog(error));
      return 'failed';
    }
  }

  /** One-button dialog used for every outcome of the recovery offer (#4936). */
  private async showRecoveryMessageAsync(message: TranslatedString) {
    await this.alertCtrl.showAlertAsync({
      title: LL.auth.newAccountMade.title(),
      message,
      buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary, result: null }],
      implicitCancelResult: null,
    });
  }

  learnMore() {
    getMyApp().redirectToLearnMore();
  }

  async oauthErrorAsync(error: any, provider: SocialSignInProvider) {
    console.error('Oauth login error:', describeErrorForLog(error));
    // Surface the real auth/* code to Sentry so we are not blind when the
    // generic "Something went wrong" alert fires — user-cancellation is
    // expected and stays silent. See #3678 for the original blind catch.
    if (error?.code !== 'auth/popup-closed-by-user') {
      this.logging.trackSilentProblem('LoginPage.oauthError', {
        provider,
        code: error?.code,
        message: error?.message ?? String(error),
        nativeCode: error?.nativeCode,
        email: error?.email,
      });
    }
    if (error?.code === 'auth/account-exists-with-different-credential') {
      // the modular Firebase SDK carries the email in customData; `email` is the older shape
      const email = error?.customData?.email ?? error?.email;
      this.waiting = true;
      await answerExistingAccountAsync({
        lookUpMethods: () => this.auth.getMethodsForEmailAsync(email),
        reportLookupFailure: failure => this.reportSignInMethodsLookupFailure(failure),
        offerAlternativeSignIn: methods => {
          this.waiting = false;
          void this.offerAlternativeSignInAsync(methods);
        },
        showSignInError: () => {
          this.waiting = false;
          return this.handleAuthErrorAsync(error);
        },
      });
    } else if (error?.code === 'auth/popup-closed-by-user') {
      this.loginError = LL.auth.errors.popupClosed();
      this.safeMarkForCheck(this.cdr);
    } else {
      await this.handleAuthErrorAsync(error);
    }

    this.safeMarkForCheck(this.cdr);
  }

  // inline errors are shown near the form fields, alert errors are shown as modal dialogs
  private static readonly FIREBASE_ERROR_MAP: Record<string, { display: 'inline' | 'alert', message: () => TranslatedString }> = {
    'auth/invalid-email':        { display: 'inline', message: () => LL.auth.errors.firebase.invalidEmail() },
    'auth/missing-email':        { display: 'inline', message: () => LL.auth.errors.firebase.missingEmail() },
    'auth/user-not-found':       { display: 'inline', message: () => LL.auth.errors.firebase.userNotFound() },
    'auth/email-already-in-use': { display: 'inline', message: () => LL.auth.errors.firebase.emailAlreadyInUse() },
    'auth/account-exists-with-different-credential': { display: 'inline', message: () => LL.auth.errors.firebase.accountExistsWithDifferentCredential() },
    'auth/wrong-password':       { display: 'inline', message: () => LL.auth.errors.firebase.wrongPassword() },
    'auth/invalid-credential':   { display: 'inline', message: () => LL.auth.errors.firebase.invalidCredential() },
    'auth/weak-password':        { display: 'inline', message: () => LL.auth.errors.firebase.weakPassword() },
    'auth/invalid-action-code':  { display: 'inline', message: () => LL.auth.errors.firebase.invalidActionCode() },
    'auth/expired-action-code':  { display: 'inline', message: () => LL.auth.errors.firebase.expiredActionCode() },
    'auth/too-many-requests':    { display: 'alert',  message: () => LL.auth.errors.firebase.tooManyRequests() },
    'auth/network-request-failed': { display: 'alert', message: () => LL.auth.errors.firebase.networkRequestFailed() },
    'auth/user-disabled':        { display: 'alert',  message: () => LL.auth.errors.firebase.userDisabled({ email: getSupportEmail() }) },
    'auth/operation-not-allowed': { display: 'alert', message: () => LL.auth.errors.firebase.operationNotAllowed() },
    'auth/popup-blocked':        { display: 'alert',  message: () => LL.auth.errors.firebase.popupBlocked() },
    'auth/requires-recent-login': { display: 'alert', message: () => LL.auth.errors.firebase.requiresRecentLogin() },
  };

  /** The lookup failing no longer blocks sign-in (#5472), but it must still reach Sentry. */
  private reportSignInMethodsLookupFailure(failure: SignInMethodsLookupFailure) {
    this.logging.trackSilentProblem('LoginPage.signInMethodsLookupFailed', { ...failure });
  }

  private async handleAuthErrorAsync(error: any) {
    let code = error?.code as string;
    console.error(`Auth error [${code || 'unknown'}]:`, error?.message || error);

    let entry = code ? LoginPage.FIREBASE_ERROR_MAP[code] : undefined;

    if (entry?.display === 'inline') {
      this.loginError = entry.message();
    } else {
      // alert for system errors or unknown codes
      let message = entry ? entry.message() : LL.auth.errors.firebase.genericError({ email: getSupportEmail() });
      await this.alertCtrl.showAlertAsync({
        message: message,
        buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary }],
      });
    }
    this.safeMarkForCheck(this.cdr);
  }

  // ─── Coach claim methods ──────────────────────────────────────

  private async validateClaimTokenAsync() {
    const result = await this.coachClaim.validateTokenAsync();
    if (this.viewDestroyed) return;
    switch (result.outcome) {
      case CoachTokenCheckOutcome.Valid:
        this.claimCoachName = result.coachName;
        this.claimWording = decideCoachInvitationWording(result.clientFills);
        this.claimScreen = CoachClaimScreen.Offer;
        // Tier 1 white-labeling (#3707): theme the claim screen from the coach's
        // branding (logo swap + accented CTAs). No client DB exists yet, so this
        // applies the override CSS vars directly from the validate result. Pass
        // cache:false — the viewer is not (yet) this coach's client, so the
        // warm-boot splash cache must not be populated from this pre-auth screen.
        this.claimBrandLogoUrl = result.branding?.logoUrl || '';
        this.claimBrandName = result.branding?.brandName || '';
        this.coachBrandingTheme.applyBranding(result.branding, { cache: false });
        break;
      case CoachTokenCheckOutcome.RejectedByServer:
        this.claimScreen = CoachClaimScreen.LinkRejected;
        break;
      case CoachTokenCheckOutcome.CouldNotCheck:
        // deliberately NOT LinkRejected: nobody ever looked at this link, so we may not call it
        // bad. The screen says so and offers Try Again — the token is unspent, so retrying works.
        this.claimScreen = CoachClaimScreen.CouldNotCheck;
        break;
    }
    this.cdr.markForCheck();
  }

  /** Ask again after a check that never got an answer. The link is unspent, so this is safe to repeat. */
  retryClaimTokenCheck() {
    console.log(`[CLAIM] login page: user chose Try Again after a check that got no answer`);
    this.claimScreen = CoachClaimScreen.Checking;
    this.cdr.markForCheck();
    void this.validateClaimTokenAsync();
  }

  // the sentence under the claim screen's title: a blank invite must not promise a ready program (#6793)
  claimDescription(): string {
    const coachName = coachInvitationName(this.claimCoachName);
    if (this.claimWording === CoachInvitationWording.BlankIntake) {
      return LL.coach.claim.descriptionBlankIntake({ coachName });
    }
    return LL.coach.claim.description({ coachName });
  }

  claimLogin() {
    console.log(`[CLAIM] login page: user chose Sign In`);
    this.switchToPhase(LoginPhase.signIn);
  }

  claimRegister() {
    console.log(`[CLAIM] login page: user chose Create Account`);
    this.switchToPhase(LoginPhase.newRegistration);
  }

  cancelClaim() {
    this.claimCoachName = '';
    this.claimWording = CoachInvitationWording.ProgramPrepared;
    this.claimScreen = CoachClaimScreen.Checking;
    // Tier 1 white-labeling (#3707): drop the coach theme when the user backs
    // out of the claim, so the plain MH login screen returns.
    this.claimBrandLogoUrl = '';
    this.claimBrandName = '';
    this.coachBrandingTheme.clearBranding();
    this.switchToPhase(LoginPhase.landing);
  }
}
